import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpInput } from "@/components/OtpInput";
import { ShieldCheck, Loader2 } from "lucide-react";
import { LOGO_FULL, getHeroPhoto, BRAND, getIdentity } from "@/lib/identity";

// Declared at module scope (NOT inside Login) so its identity is stable across
// re-renders — otherwise React remounts it (and OtpInput) on every keystroke,
// wiping refs and breaking OTP focus/auto-advance.
function FormBlock({
  dark = false, accent = "#E0781A",
  stage, email, setEmail, password, setPassword, newPw, setNewPw, newPw2, setNewPw2,
  enroll, code, setCode, error, loading, submitCredentials, submitNewPassword, submitCode,
  verifyCode, setStage, setError,
}) {
  const labelCls = `text-xs font-bold uppercase tracking-wider ${dark ? "text-zinc-400" : "text-slate-500"}`;
  const inputCls = `rounded-lg mt-1 font-mono text-sm focus:ring-1 ${dark
    ? "bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-[#E0781A] focus:ring-[#E0781A]"
    : "border-slate-300 focus:border-[#E0781A] focus:ring-[#E0781A]"}`;
  const btnStyle = { backgroundColor: accent };
  return (
    <>
      {stage === "credentials" && (
        <form onSubmit={submitCredentials} data-testid="credentials-form">
          <h2 className={`font-display text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Sign in</h2>
          <p className={`text-sm mb-6 ${dark ? "text-zinc-500" : "text-slate-500"}`}>Step 1 of 2 — credentials</p>
          <div className="space-y-4">
            <div>
              <Label className={labelCls}>Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputCls} placeholder="you@friedrichsauto.com" />
            </div>
            <div>
              <Label className={labelCls}>Password</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} placeholder="••••••••" />
            </div>
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-500 mt-3 font-mono">{error}</p>}
          <Button data-testid="login-submit-button" type="submit" disabled={loading} style={btnStyle}
            className="w-full rounded-lg hover:opacity-90 text-white mt-6 h-11 font-bold shadow-sm transition-opacity">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue →"}
          </Button>
        </form>
      )}
      {stage === "set_password" && (
        <form onSubmit={submitNewPassword} data-testid="set-password-form">
          <h2 className={`font-display text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Set your password</h2>
          <p className={`text-sm mb-5 ${dark ? "text-zinc-500" : "text-slate-500"}`}>Your account uses a temporary password — choose your own to continue.</p>
          <div className="space-y-4">
            <div>
              <Label className={labelCls}>New password</Label>
              <Input data-testid="new-password" type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)}
                className={inputCls} placeholder="••••••••••••" />
            </div>
            <div>
              <Label className={labelCls}>Confirm new password</Label>
              <Input data-testid="confirm-password" type="password" required value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                className={inputCls} placeholder="••••••••••••" />
            </div>
          </div>
          <p className={`text-[11px] mt-2 font-mono ${dark ? "text-zinc-500" : "text-slate-500"}`}>Min 12 characters · checked against a breached-password list.</p>
          {error && <p data-testid="set-password-error" className="text-sm text-red-500 mt-3 font-mono">{error}</p>}
          <Button data-testid="set-password-button" type="submit" disabled={loading || newPw.length < 12 || newPw2.length < 12} style={btnStyle}
            className="w-full rounded-lg hover:opacity-90 text-white mt-5 h-11 font-bold shadow-sm transition-opacity">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set password & continue →"}
          </Button>
        </form>
      )}
      {(stage === "enroll" || stage === "totp") && (
        <form onSubmit={submitCode} data-testid="totp-form">
          <h2 className={`font-display text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Two-factor</h2>
          <p className={`text-sm mb-5 ${dark ? "text-zinc-500" : "text-slate-500"}`}>Step 2 of 2 — authenticator code</p>
          {stage === "enroll" && enroll && (
            <div className={`rounded-xl p-4 mb-5 shadow-sm ${dark ? "border border-zinc-800 bg-zinc-900" : "border border-slate-200 bg-white"}`} data-testid="enroll-block">
              <div className={labelCls + " mb-2"}>First login — enroll</div>
              <p className={`text-xs mb-3 ${dark ? "text-zinc-400" : "text-slate-600"}`}>Scan in Google Authenticator or Authy:</p>
              <img data-testid="enroll-qr" src={enroll.qr_code} alt="TOTP QR" className="w-44 h-44 border border-slate-200 mx-auto bg-white" />
              <div className="mt-3">
                <div className={labelCls}>Manual key</div>
                <code data-testid="enroll-secret" className={`text-xs break-all block p-2 mt-1 ${dark ? "bg-zinc-950 text-zinc-300" : "bg-slate-100"}`}>{enroll.manual_secret}</code>
              </div>
            </div>
          )}
          <Label className={labelCls}>6-digit code</Label>
          <div className="mt-2 mb-1" data-testid="totp-input">
            <OtpInput value={code} onChange={setCode} onComplete={(c) => verifyCode(c)} dark={dark} disabled={loading} />
          </div>
          {error && <p data-testid="totp-error" className="text-sm text-red-500 mt-3 font-mono">{error}</p>}
          <Button data-testid="verify-totp-button" type="submit" disabled={loading || code.length !== 6} style={btnStyle}
            className="w-full rounded-lg hover:opacity-90 text-white mt-5 h-11 font-bold shadow-sm transition-opacity">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-2" /> Verify &amp; sign in</>)}
          </Button>
          <button type="button" onClick={() => { setStage("credentials"); setCode(""); setError(""); }}
            className={`w-full text-xs mt-3 underline ${dark ? "text-zinc-500" : "text-slate-500"}`} data-testid="back-to-credentials">
            ← Back
          </button>
        </form>
      )}
    </>
  );
}


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const dir = getIdentity();
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [enroll, setEnroll] = useState(null);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  // SEC-09: a 401 anywhere redirects here with ?expired=1 — say it plainly, never a dead spinner.
  const [error, setError] = useState(() =>
    (new URLSearchParams(window.location.search).get("expired") ? "Your session ended — please log in again." : ""));
  const [loading, setLoading] = useState(false);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setChallenge(data.challenge_token);
      if (data.stage === "set_password") { setStage("set_password"); }
      else if (data.stage === "enroll") { setEnroll(data); setStage("enroll"); }
      else setStage("totp");
    } catch (e2) {
      setError(apiErrorMessage(e2));
    } finally { setLoading(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== newPw2) { setError("The two passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-initial-password", { challenge_token: challenge, new_password: newPw });
      setChallenge(data.challenge_token);
      setNewPw(""); setNewPw2("");
      if (data.stage === "enroll") { setEnroll(data); setStage("enroll"); }
      else setStage("totp");
    } catch (e2) {
      setError(apiErrorMessage(e2));
    } finally { setLoading(false); }
  };

  const verifyCode = async (codeOverride) => {
    const c = codeOverride || code;
    if (loading || c.length !== 6) return;
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-totp", { challenge_token: challenge, code: c });
      await login(data.user);
      navigate("/");
    } catch (e2) {
      setError(apiErrorMessage(e2));
      setCode("");
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    verifyCode();
  };

  const formProps = {
    stage, email, setEmail, password, setPassword, newPw, setNewPw, newPw2, setNewPw2,
    enroll, code, setCode, error, loading, submitCredentials, submitNewPassword, submitCode,
    verifyCode, setStage, setError,
  };

  const HeroLayer = ({ overlay }) => {
    const photo = getHeroPhoto();
    return photo ? (
      <>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${photo}')` }} />
        <div className={`absolute inset-0 ${overlay}`} />
      </>
    ) : null;
  };

  // ============ DIRECTION 4 — Bay Door (cinematic, hero-photo-ready) ============
  if (dir === 4) {
    return (
      <div data-testid="login-page" data-identity="4" className="min-h-screen flex flex-col lg:flex-row bg-black">
        <div className="relative flex-1 id-ember id-vignette id-ticks overflow-hidden min-h-[42vh] lg:min-h-screen">
          <div className="absolute inset-0 id-grid opacity-60" />
          <HeroLayer overlay="bg-black/72" />
          {/* oversized ghosted swoosh watermark */}
          <div className="absolute -right-40 top-1/3 w-[680px] h-[680px] rounded-full border-[60px] border-white/[0.03] -rotate-12" />
          <div className="absolute inset-0 flex flex-col justify-between p-10 sm:p-16">
            <div className="flex items-center justify-between id-fadein">
              <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-14 sm:h-16 w-auto drop-shadow-lg" data-testid="login-logo" />
              <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-emerald-400/90">
                <span className="w-2 h-2 rounded-full bg-emerald-400 id-pulse" /> System Online
              </span>
            </div>
            <div className="id-fadeup max-w-2xl">
              <div className="flex items-center gap-2 mb-5">
                <span className="h-px w-10 bg-[#E0781A]" />
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#E0781A]">Employee Portal</span>
              </div>
              <h1 className="id-metal-text font-black leading-[0.95] tracking-tight" style={{ fontFamily: "'Exo 2', sans-serif", fontSize: "clamp(2.4rem,5vw,4.6rem)", fontStyle: "italic" }}>
                USED TRUCK PARTS<br />&amp; EQUIPMENT
              </h1>
              <p className="text-slate-300/90 text-base sm:text-lg mt-6 max-w-lg">{BRAND.division}</p>
              <p className="text-slate-500 text-xs mt-1.5 font-mono uppercase tracking-[0.18em]">{BRAND.legal}</p>
            </div>
            <div className="flex items-center gap-3 text-slate-400 id-fadein">
              <span className="h-px w-8 bg-white/20" />
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase">{BRAND.heritage}</span>
            </div>
          </div>
        </div>
        <div className="hidden lg:block w-px id-scan bg-white/10" />
        <div className="w-full lg:w-[460px] shrink-0 bg-zinc-950 id-hazard flex items-center justify-center p-7 sm:p-12">
          <div className="w-full max-w-sm id-fadeup">
            <div className="lg:hidden mb-7 text-center">
              <span className="font-black text-white text-xl" style={{ fontFamily: "'Exo 2', sans-serif", fontStyle: "italic" }}>{BRAND.portal}</span>
            </div>
            <FormBlock {...formProps} dark accent="#E0781A" />
          </div>
        </div>
      </div>
    );
  }

  // ============ DIRECTION 5 — Spec Sheet (engineering drawing) ============
  if (dir === 5) {
    return (
      <div data-testid="login-page" data-identity="5" className="min-h-screen id-onyx relative flex items-center justify-center p-4 sm:p-8 overflow-hidden">
        <div className="absolute inset-0 id-grid opacity-70" />
        <HeroLayer overlay="bg-black/88" />
        <div className="relative w-full max-w-md id-fadeup">
          <div className="relative rounded-sm border border-zinc-700 bg-zinc-950/85 backdrop-blur-sm id-ticks overflow-hidden">
            {/* title block — top */}
            <div className="flex items-stretch border-b border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              <div className="px-4 py-2 border-r border-zinc-800">DWG · FAS-PORTAL</div>
              <div className="px-4 py-2 border-r border-zinc-800 hidden sm:block">REV · 1954</div>
              <div className="px-4 py-2 ml-auto text-[#E0781A]">SECURE / 2FA</div>
            </div>
            <div className="p-7 sm:p-10">
              <div className="flex flex-col items-center text-center border-b border-dashed border-zinc-800 pb-7 mb-7">
                <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-20 w-auto drop-shadow" data-testid="login-logo" />
                <h1 className="mt-4 text-white font-black tracking-tight text-xl sm:text-2xl" style={{ fontFamily: "Chivo, sans-serif" }}>{BRAND.portal}</h1>
                <p className="text-zinc-600 text-[11px] mt-2">{BRAND.division}</p>
                <p className="text-zinc-500 text-[10px] mt-1.5 font-mono uppercase tracking-[0.18em]">{BRAND.legal}</p>
              </div>
              <FormBlock {...formProps} dark accent="#E0781A" />
            </div>
            {/* title block — bottom */}
            <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
              <span>{BRAND.heritageShort}</span>
              <span className="text-zinc-600">Family Owned &amp; Operated</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ DIRECTION 6 — Forge (warm metal, dramatic) ============
  if (dir === 6) {
    return (
      <div data-testid="login-page" data-identity="6" className="min-h-screen id-ember id-vignette relative flex items-center justify-center p-5 sm:p-8 overflow-hidden">
        <HeroLayer overlay="bg-black/82" />
        <div className="absolute inset-x-0 bottom-0 h-40 id-hazard opacity-40" />
        <div className="relative w-full max-w-md text-center id-fadeup">
          <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-28 sm:h-36 w-auto mx-auto drop-shadow-2xl mb-6 id-fadein" data-testid="login-logo" />
          <p className="text-zinc-300 text-sm">{BRAND.portal}</p>
          <div className="flex items-center justify-center gap-3 my-6">
            <span className="h-px w-12 bg-white/15" />
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-slate-400">{BRAND.heritage}</span>
            <span className="h-px w-12 bg-white/15" />
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-900/70 backdrop-blur-sm id-glow-amber p-7 sm:p-9 text-left">
            <FormBlock {...formProps} dark accent="#E0781A" />
          </div>
          <p className="text-zinc-600 text-[11px] mt-6">{BRAND.division}</p>
          <p className="text-zinc-700 text-[10px] mt-1 font-mono uppercase tracking-[0.2em]">{BRAND.legal}</p>
        </div>
      </div>
    );
  }

  // ============ DIRECTION 3 — Classic Heavy-Duty (asymmetric red block) ============
  if (dir === 3) {
    return (
      <div data-testid="login-page" data-identity="3" className="min-h-screen grid lg:grid-cols-[4fr_3fr]">
        <div className="relative overflow-hidden bg-[#A81018] id-noise flex flex-col justify-between p-10 sm:p-14 min-h-[40vh] lg:min-h-screen">
          <HeroLayer overlay="bg-[#A81018]/75 mix-blend-multiply" />
          <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full border-[40px] border-white/5" />
          <div className="relative font-display text-white/70 text-sm uppercase tracking-[0.25em] id-fadein">{BRAND.heritage}</div>
          <div className="relative id-fadeup">
            <span className="inline-block rounded-xl bg-black/35 px-5 py-3 ring-1 ring-white/10">
              <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-12 sm:h-16 w-auto drop-shadow-lg" data-testid="login-logo" />
            </span>
            <h1 className="hero-block mt-6 text-white leading-[0.92]" style={{ fontFamily: "Anton, sans-serif", fontSize: "clamp(2.5rem,6vw,5rem)", textTransform: "uppercase" }}>
              Used Truck Parts<br />&amp; Equipment
            </h1>
            <p className="mt-5 text-white/85 text-base max-w-md">{BRAND.division} — the internal employee portal.</p>
            <p className="mt-1 text-white/60 text-xs font-mono uppercase tracking-[0.18em]">{BRAND.legal}</p>
          </div>
          <div className="relative font-mono text-[11px] text-white/55 tracking-widest uppercase">{BRAND.portal}</div>
        </div>
        <div className="flex items-center justify-center p-6 sm:p-10 bg-[#F4F4F5]">
          <div className="w-full max-w-sm id-fadeup">
            <FormBlock {...formProps} dark={false} accent="#E0781A" />
          </div>
        </div>
      </div>
    );
  }

  // ============ DIRECTION 2 — Industrial Precision (photo-free console) ============
  if (dir === 2) {
    return (
      <div data-testid="login-page" data-identity="2" className="min-h-screen id-onyx relative flex items-center justify-center p-4 sm:p-6 overflow-hidden">
        <div className="absolute inset-0 id-hazard" />
        <HeroLayer overlay="bg-black/85 grayscale" />
        <div className="absolute inset-x-0 top-0 h-1 bg-[#A81018]" />
        <div className="relative w-full max-w-md id-fadeup">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/80 backdrop-blur-sm id-glow-amber p-7 sm:p-10">
            <div className="text-center border-b border-zinc-800 pb-6 mb-7">
              <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-24 sm:h-28 w-auto mx-auto drop-shadow-lg" data-testid="login-logo" />
              <p className="text-zinc-300 text-sm mt-4">{BRAND.portal}</p>
              <p className="text-zinc-500 text-[11px] mt-2">{BRAND.division}</p>
              <p className="text-zinc-600 text-[10px] mt-1 font-mono uppercase tracking-[0.18em]">{BRAND.legal}</p>
            </div>
            <FormBlock {...formProps} dark accent="#E0781A" />
          </div>
          <div className="text-center font-mono text-[10px] text-zinc-600 tracking-[0.25em] uppercase mt-5">[ Est. 1954 // Family Owned &amp; Operated ]</div>
        </div>
      </div>
    );
  }

  // ============ DIRECTION 1 — Modern Heritage (default, balanced) ============
  return (
    <div data-testid="login-page" data-identity="1" className="min-h-screen grid lg:grid-cols-2">
      <div className="relative overflow-hidden id-steel text-white flex flex-col justify-between p-10 sm:p-14 min-h-[36vh] lg:min-h-screen">
        <div className="absolute inset-0 id-grid" />
        <div className="absolute -top-24 -left-24 w-[380px] h-[380px] rounded-full bg-[#A81018]/20 blur-3xl" />
        <HeroLayer overlay="bg-slate-900/70 mix-blend-multiply" />
        <div className="absolute inset-x-0 top-0 h-1 bg-[#A81018]" />
        <div className="relative flex items-center gap-3 id-fadein">
          <span className="font-mono text-[10px] text-slate-400 tracking-[0.3em] uppercase">Employee Portal</span>
        </div>
        <div className="relative id-fadeup">
          <img src={LOGO_FULL} alt="FAS — Used Truck Parts & Equipment" className="h-12 sm:h-14 w-auto drop-shadow" data-testid="login-logo" />
          <p className="text-sm text-slate-400 mt-5">{BRAND.division}</p>
          <p className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-[0.18em]">{BRAND.legal}</p>
          <h1 className="font-display font-black text-white text-3xl sm:text-4xl mt-7 leading-tight tracking-tight">{BRAND.specialty}</h1>
          <p className="text-sm text-slate-400 mt-4 max-w-sm leading-relaxed">
            The internal parts &amp; inventory system. Secured with mandatory two-factor authentication — permissions enforced on every request.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-slate-400">
          <span className="h-px w-8 bg-[#E0781A]" />
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase">{BRAND.heritage}</span>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[#F8FAFC]">
        <div className="w-full max-w-sm id-fadeup">
          <div className="lg:hidden mb-8">
            <span className="font-display font-black tracking-tight text-slate-900 text-lg">{BRAND.portal}</span>
            <div className="font-mono text-[10px] text-slate-400 tracking-[0.2em] uppercase mt-0.5">{BRAND.heritage}</div>
          </div>
          <FormBlock {...formProps} dark={false} accent="#E0781A" />
        </div>
      </div>
    </div>
  );
}
